import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {EMOJI_MAP, type ReactionContent} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import {useNavigate} from '@github-ui/use-navigate'
import {SortAscIcon, SortDescIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {LABELS} from '../../constants/labels'
import {parseQuery, replaceSortInQuery} from '../../utils/query'
import styles from './sorting-dropdown.module.css'

const SORT_KEY_MATCHING_REGEX = /(created|updated|comments|reactions|relevance?)/
const SORT_DIRECTION_ICONS = {
  asc: SortAscIcon,
  desc: SortDescIcon,
}
export const SORT_KEY_TO_OPTION_DISPLAY_VALUE = {
  ...LABELS.sortDropdownOptionDisplayValues,
  ...LABELS.sortDropdownReactionLabels,
  reactions: LABELS.totalReactions,
}

type SortingDirection = 'asc' | 'desc'
type SortKey = keyof typeof SORT_KEY_TO_OPTION_DISPLAY_VALUE

export type SortingDropdownProps = {
  activeSearchQuery: string
  dirtySearchQuery: string | null
  setSortingItemSelected: (arg: string) => void
  searchUrl: (query?: string) => string
  setCurrentPage: (page: number) => void
  nested?: boolean
  bestMatchSortEnabled?: boolean
  onQueryChange?: (queryText: string) => void
}

export function SortingDropdown({
  activeSearchQuery,
  dirtySearchQuery,
  setSortingItemSelected,
  searchUrl,
  setCurrentPage,
  nested = false,
  bestMatchSortEnabled,
  onQueryChange,
}: SortingDropdownProps) {
  const sortQuery = useMemo(() => parseQuery(activeSearchQuery).get('sort')?.[0] || '', [activeSearchQuery])

  const [direction, setDirection] = useState<SortingDirection>(() => getDirectionFromQuery(sortQuery))
  const [selectedOptionKey, setSelectedOptionKey] = useState<SortKey>(() =>
    getSortOptionKeyFromQuery(sortQuery, activeSearchQuery, bestMatchSortEnabled),
  )

  const navigate = useNavigate()

  const updateQuery = useCallback(
    (key: SortKey, sortDirection: SortingDirection) => {
      const queryText = replaceSortInQuery(dirtySearchQuery || '', `${LABELS.sortKeyToQuery[key]}-${sortDirection}`)
      if (onQueryChange) {
        onQueryChange(queryText)
        return
      }
      const url = searchUrl(queryText)
      navigate(url)
      setCurrentPage(1)
    },
    [dirtySearchQuery, onQueryChange, navigate, searchUrl, setCurrentPage],
  )

  const onSelectOption = useCallback(
    (value: string, key: SortKey) => {
      setSortingItemSelected(value)

      if (key === 'relevance' && direction === 'asc') {
        setDirection('desc')
        updateQuery(key, 'desc')
      } else {
        updateQuery(key, direction)
      }
    },
    [direction, updateQuery, setSortingItemSelected],
  )

  const onSelectReactions = useCallback(
    (key: SortKey) => {
      if (direction === 'asc') {
        setDirection('desc')
      }
      updateQuery(key, 'desc')
      setSortingItemSelected(key)
    },
    [direction, updateQuery, setSortingItemSelected],
  )

  const onSelectDirection = useCallback(
    (value: SortingDirection) => {
      if (selectedOptionKey) {
        setDirection(value)
        setSortingItemSelected(SORT_KEY_TO_OPTION_DISPLAY_VALUE[selectedOptionKey])
        updateQuery(selectedOptionKey, value)
      }
    },
    [updateQuery, selectedOptionKey, setSortingItemSelected],
  )

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDirection(getDirectionFromQuery(sortQuery))
    setSelectedOptionKey(getSortOptionKeyFromQuery(sortQuery, activeSearchQuery, bestMatchSortEnabled))

    const selectedKey = getSelectedSortOptionKeyFromQuery(activeSearchQuery, bestMatchSortEnabled)
    setSortingItemSelected(selectedKey)
  }, [setSortingItemSelected, sortQuery, activeSearchQuery, bestMatchSortEnabled])

  const sortingLabelKeyValuePairs = Object.entries(LABELS.sortDropdownOptionDisplayValues) as Array<[SortKey, string]>
  const sortingEmojiKeyValuePairs = Object.entries(LABELS.sortDropdownReactionLabels) as Array<[SortKey, string]>
  const disableDirection = isEmojiSelected(selectedOptionKey) || selectedOptionKey === 'relevance'

  const secondaryReactionMenu = (
    <>
      <ActionList.Item
        key="reactions"
        onSelect={() => onSelectOption(LABELS.totalReactions, 'reactions')}
        selected={selectedOptionKey === 'reactions'}
        role="menuitemradio"
      >
        {LABELS.totalReactions}
      </ActionList.Item>
      <ActionList.Divider />
      {sortingEmojiKeyValuePairs.map(([sortKey, displayValue]) => (
        <ActionList.Item
          key={sortKey}
          onSelect={() => onSelectReactions(sortKey)}
          selected={selectedOptionKey === sortKey}
          role="menuitemradio"
        >
          <ActionList.LeadingVisual>{EMOJI_MAP[handleEmojiMapValue(sortKey) || '']}</ActionList.LeadingVisual>
          {displayValue}
        </ActionList.Item>
      ))}
    </>
  )

  const primaryMenu = (
    <>
      <ActionList.Group>
        <ActionList.GroupHeading>Sort by</ActionList.GroupHeading>
        {sortingLabelKeyValuePairs.map(([sortKey, displayValue]) => (
          <ActionList.Item
            key={sortKey}
            onSelect={() => onSelectOption(displayValue, sortKey)}
            selected={selectedOptionKey === sortKey}
            role="menuitemradio"
          >
            {displayValue}
          </ActionList.Item>
        ))}
        <ActionMenu>
          <ActionMenu.Anchor>
            <ActionList.Item selected={isEmojiSelected(selectedOptionKey) || selectedOptionKey === 'reactions'}>
              {LABELS.reactions}
            </ActionList.Item>
          </ActionMenu.Anchor>

          <ActionMenu.Overlay>
            <ActionList selectionVariant="single">{secondaryReactionMenu}</ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </ActionList.Group>
      <ActionList.Divider />
      <ActionList.Group>
        <ActionList.GroupHeading>Order</ActionList.GroupHeading>
        <ActionList.Item
          disabled={disableDirection}
          key="ascending"
          selected={direction === 'asc' && selectedOptionKey !== null}
          onSelect={() => onSelectDirection('asc')}
        >
          <ActionList.LeadingVisual>
            <SortAscIcon />
          </ActionList.LeadingVisual>
          {getDirectionLabel(selectedOptionKey, 'asc')}
        </ActionList.Item>
        <ActionList.Item
          disabled={disableDirection}
          key="descending"
          selected={direction === 'desc' && selectedOptionKey !== null}
          onSelect={() => onSelectDirection('desc')}
        >
          <ActionList.LeadingVisual>
            <SortDescIcon />
          </ActionList.LeadingVisual>
          {getDirectionLabel(selectedOptionKey, 'desc')}
        </ActionList.Item>
      </ActionList.Group>
    </>
  )

  if (nested)
    return (
      <ErrorBoundary fallback={null}>
        {/* `aria-labelledby=""` is a hack to unset/override ActionList’s built-in `ariaLabelledBy`: https://github.com/primer/react/blob/74fd6d9348a82b820604601cab16657b855a49ba/packages/react/src/ActionList/List.tsx#L46. Without this hack, this list would take its name from the “…” menu (`packages/action-bar/src/OverflowMenu.tsx`), resulting in two menus named “More Actions”. “Sort” is a better/clearer name. */}
        <ActionList selectionVariant="single" aria-labelledby="" aria-label="Sort">
          {primaryMenu}
        </ActionList>
      </ErrorBoundary>
    )

  return (
    <ActionMenu>
      <ActionMenu.Button
        variant="invisible"
        className={styles.sortingMenuButton}
        leadingVisual={SORT_DIRECTION_ICONS[direction || 'desc']}
      >
        {isValidSortKey(selectedOptionKey ?? '') ? <span className="sr-only">Sort by </span> : null}
        <>{selectedOptionKey ? getSortOptionButtonLabel(selectedOptionKey, direction || 'desc') : LABELS.sort}</>
        {isValidSortKey(selectedOptionKey ?? '') ? (
          <span className="sr-only">{direction === 'asc' ? ', ascending' : ', descending'}</span>
        ) : null}
      </ActionMenu.Button>

      <ActionMenu.Overlay>
        <ActionList selectionVariant="single">{primaryMenu}</ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

export function getSelectedSortOptionKeyFromQuery(query: string, bestMatchSortEnabled?: boolean): SortKey {
  const parsedQuery = parseQuery(query)
  const sortValue = parsedQuery.get('sort')?.[0] || ''

  return getSortOptionKeyFromQuery(sortValue, query, bestMatchSortEnabled)
}

function hasSearchTerms(query: string): boolean {
  const parsedQuery = parseQuery(query)
  const searchTerms = parsedQuery.get('searchTerms') || []
  return searchTerms.length > 0
}

function getDefaultSortKey(query: string, bestMatchSortEnabled?: boolean): SortKey {
  if (!bestMatchSortEnabled) {
    // Best match sort not enabled: always default to created (newest)
    return 'created'
  }

  // Best match sort enabled: If query has free text (search terms), default to relevance
  // Otherwise, default to created (newest)
  return hasSearchTerms(query) ? 'relevance' : 'created'
}

export function handleEmojiMapValue(sortKey: SortKey): ReactionContent | undefined {
  const emoji = SORT_KEY_TO_OPTION_DISPLAY_VALUE[sortKey]?.replace(' ', '_').toUpperCase()
  return isValidEmoji(emoji) ? emoji : undefined
}

export function isReactionSort(sortKey: string): boolean {
  return sortKey === 'reactions' || !!handleEmojiMapValue(sortKey as SortKey)
}

export function getReactionDisplayForSort(sortKey: string): {reaction: string; reactionEmoji: string} | undefined {
  const reactionContent = handleEmojiMapValue(sortKey as SortKey)
  if (!reactionContent) return undefined
  const emoji = EMOJI_MAP[reactionContent]
  if (!emoji) return undefined
  return {reaction: reactionContent, reactionEmoji: emoji}
}

function getDirectionFromQuery(query: string): SortingDirection {
  const direction = query.split('-')?.[1]

  if (direction === 'asc' || direction === 'desc') {
    return direction as SortingDirection
  }

  return 'desc'
}

export function getSortOptionKeyFromQuery(
  sortValue: string,
  fullQuery: string,
  bestMatchSortEnabled?: boolean,
): SortKey {
  if (!sortValue) {
    return getDefaultSortKey(fullQuery, bestMatchSortEnabled)
  }

  let key = sortValue.match(SORT_KEY_MATCHING_REGEX)?.[0]

  if (key === 'reactions') {
    key = sortValue.split(/-asc|-desc/)?.[0]?.split('reactions-')?.[1] || 'reactions'
  }

  return isValidSortKey(key) ? key : getDefaultSortKey(fullQuery, bestMatchSortEnabled)
}

function getSortOptionButtonLabel(sortKey: SortKey, direction: SortingDirection): string {
  if (sortKey === 'created') {
    return LABELS.sortDropdownMenuButtonLabels[sortKey][direction]
  }

  return LABELS.sortDropdownMenuButtonLabels[sortKey]
}

function getDirectionLabel(sortKey: SortKey | null, direction: SortingDirection): string {
  if (sortKey === 'created' || sortKey === 'updated') {
    return direction === 'asc' ? LABELS.Oldest : LABELS.Newest
  }

  return direction === 'asc' ? LABELS.ascending : LABELS.descending
}

function isEmojiSelected(sortKey: SortKey | null): boolean {
  return sortKey ? LABELS.sortDropdownReactionLabels.hasOwnProperty(sortKey) : false
}

function isValidSortKey(sortKey: string | undefined): sortKey is SortKey {
  if (!sortKey) {
    return false
  }

  return Object.keys(SORT_KEY_TO_OPTION_DISPLAY_VALUE).includes(sortKey)
}

function isValidEmoji(emoji: string): emoji is ReactionContent {
  return Object.keys(EMOJI_MAP).includes(emoji)
}
